import * as React from 'react';
import { Button, Text, Card, CardBody, UnorderedList, ListItem } from '@chakra-ui/react';
import {
  UploadButtonSection,
  UploadActions,
  UploadExample,
  RadioOptionsWithConfirm,
  UploadComplete as UploadCompleteSection,
  AcknowledgeBox,
  ModalColumns,
  Link,
  RequirementsSummary
} from 'shared-components';
import placeholderExample from '../../../../assets/images/sample-parent-consent-to-travel-letter.jpg';

interface IProps {
  customDocumentLink?: string;
  complete: boolean;
  isSigned?: boolean;
  isDated?: boolean;
  isDocumentFixed?: boolean;
  fileNames: string[];
  onContinue: () => void;
  onUpload: (metaData?: any) => void;
  onClose: () => void;
  docKey: string;
  infoOnly: boolean;
  docStatus: string;
  onAcknowledgeToggle: (status: string) => void;
}

const ParentalConsentModal: React.FC<IProps> = props => {
  const {
    isSigned,
    isDated,
    complete,
    isDocumentFixed,
    docStatus,
    customDocumentLink,
    docKey,
    infoOnly,
    fileNames,
    onUpload,
    onAcknowledgeToggle,
    onClose,
    onContinue
  } = props;

  let hasErrors = false;
  if (!isDocumentFixed) {
    if (isSigned !== undefined && isDated !== undefined) {
      hasErrors = !isSigned || !isDated;
    }
  }

  const [state, setState] = React.useState({
    isPage1: complete || hasErrors || isDocumentFixed ? false : true,
    isPage2: false,
    uploadComplete: complete,
    hasErrors,
    haveDocument: isSigned !== undefined ? true : undefined,
    isSigned: isSigned,
    isDated: isDated
  });

  const handleYesNoClick = (target: string, question: any) => {
    setState(prevState => ({ ...prevState, [target]: question.char === 'Y' ? true : false }));
  };

  const handleUpload = (isFix?: boolean) => {
    onUpload({
      isSigned: state.isSigned,
      isDated: state.isDated,
      shouldShowRequirements: !state.isDated || !state.isSigned,
      isDocumentFixed: !!isFix
    });
  };

  const handleOnClose = () => {
    if (docStatus === 'INFO_ONLY') {
      onAcknowledgeToggle('NOT_ACKNOWLEDGED');
    }
    onClose();
  };

  const toggleAcknowledge = (key: string, value: boolean) => {
    // prevent call to API if previous call not yet complete
    if ((value === true && docStatus !== 'ACKNOWLEDGED') || (value === false && docStatus === 'ACKNOWLEDGED')) {
      onAcknowledgeToggle(value === true ? 'ACKNOWLEDGED' : 'NOT_ACKNOWLEDGED');
    }
  };

  return (
    <ModalColumns
      id={'parentalConsent'}
      title={'Parental Consent Letter'}
      onClose={handleOnClose}
      supportingColumn={<UploadExample altText="Parent consent to travel letter example" url={placeholderExample} />}
    >
      {infoOnly ? (
        <>
          <Text>
            As part of your visa application, you may be asked to provide a letter from your parent or guardian that
            acknowledges and gives consent for your travel to the UK for study.
          </Text>

          <AcknowledgeBox
            id={docKey}
            blurb="Please confirm you have read and understand the above."
            isTicked={docStatus === 'ACKNOWLEDGED'}
            onChange={toggleAcknowledge}
          />

          <UploadActions
            complete={docStatus === 'ACKNOWLEDGED'}
            onContinue={handleOnClose}
            label="Return to document upload"
          />
        </>
      ) : (
        <>
          {state.isPage1 && (
            <>
              <>
                <Text>
                  Due to your age, we require a letter from your parent or guardian to acknowledge and give consent for
                  your travel to the UK for study.
                </Text>

                {customDocumentLink ? (
                  <Card variant="filled" mb={6}>
                    <CardBody>
                      <Text>Please ensure your parent/guardian fills out all empty fields in the form below:</Text>

                      <Text mb={0}>
                        <Link href={customDocumentLink} isExternal>
                          Parental Consent Form Download
                        </Link>
                      </Text>
                    </CardBody>
                  </Card>
                ) : null}
              </>

              <RadioOptionsWithConfirm
                key={'haveLetter'}
                value={state.haveDocument}
                label={'Do you have a letter yet?'}
                onClick={(question: any) => handleYesNoClick('haveDocument', question)}
                isInline={true}
                questions={[
                  {
                    char: 'Y',
                    label: 'Yes',
                    selected: state.haveDocument === true
                  },
                  {
                    char: 'N',
                    label: 'Not yet',
                    selected: state.haveDocument === false
                  }
                ]}
              />

              {state.haveDocument === true && (
                <>
                  <RadioOptionsWithConfirm
                    key={'isSigned'}
                    value={state.isSigned}
                    label={'Is the letter signed by your parent or guardian?'}
                    onClick={(question: any) => handleYesNoClick('isSigned', question)}
                    isInline={true}
                    questions={[
                      {
                        char: 'Y',
                        label: 'Yes',
                        selected: state.isSigned === true
                      },
                      {
                        char: 'N',
                        label: 'No',
                        selected: state.isSigned === false
                      }
                    ]}
                  />

                  <RadioOptionsWithConfirm
                    key={'isDated'}
                    value={state.isDated}
                    label={'Is your letter dated?'}
                    onClick={(question: any) => handleYesNoClick('isDated', question)}
                    isInline={true}
                    questions={[
                      {
                        char: 'Y',
                        label: 'Yes',
                        selected: state.isDated === true
                      },
                      {
                        char: 'N',
                        label: 'No',
                        selected: state.isDated === false
                      }
                    ]}
                  />

                  <Button
                    onClick={() => {
                      setState(prevState => ({ ...prevState, isPage2: true, isPage1: false }));
                      handleUpload();
                    }}
                    disabled={state.isSigned === undefined || state.isDated === undefined}
                  >
                    Continue
                  </Button>
                </>
              )}

              {state.haveDocument === false && (
                <>
                  <RequirementsSummary title={'Please make sure your letter meets the following requirements:'}>
                    <UnorderedList>
                      <ListItem>Letter must be signed by your parent or guardian</ListItem>
                      <ListItem>Letter must include a date</ListItem>
                    </UnorderedList>
                  </RequirementsSummary>

                  <UploadActions complete={true} onContinue={onClose} label={'Return to document upload'} />
                </>
              )}
            </>
          )}

          {state.isPage2 && (
            <>
              <UploadButtonSection onUpload={handleUpload}>
                Awaiting upload of <b>Parental Consent Letter</b>
              </UploadButtonSection>

              <UploadActions complete={complete} onContinue={onContinue} />
            </>
          )}

          {((state.uploadComplete && !state.hasErrors) || isDocumentFixed) && (
            <UploadCompleteSection fileNames={fileNames} onUpload={handleUpload} onContinue={onContinue} />
          )}

          {!isDocumentFixed && state.hasErrors && (
            <>
              <RequirementsSummary title={'Further action is required'} uploadWarning>
                <Text>Your letter does not meet all of the requirements:</Text>

                <UnorderedList mb={4}>
                  <ListItem>Letter must be signed by your parent or guardian</ListItem>
                  <ListItem>Letter must include a date</ListItem>
                </UnorderedList>

                <Text mb={0}>
                  You will need to upload a new document after you have fixed the issues with the current letter.
                </Text>
              </RequirementsSummary>

              <UploadButtonSection onUpload={() => handleUpload(true)}>
                Awaiting upload of <b>New Parental Consent Letter</b>
              </UploadButtonSection>

              <UploadActions complete={false} onContinue={onContinue} />
            </>
          )}
        </>
      )}
    </ModalColumns>
  );
};

export default ParentalConsentModal;
